<template>
  <modal
    v-if="show"
    width="600px"
    height="700px"
    bodyPadding="0"
    :title="`${t('Add custom domain')} (${step}/3)`"
    @close="checkClose"
    :read-article-url="`https://docs.bemob.com/${locale}/custom-domains`"
  >
    <template v-if="step === 1">
      <div class="dialog-wrapper add-custom-domain">
        <div class="w100">
          <div class="horizontal-steps">
            <ui-radio-group
              v-model="step"
              class="radio_group"
              styleType="horizontal-line"
            >
              <ui-radio
                class="circle pl-12 pr-12"
                :value="1"
                :label="t('Setting the CNAME')"
              />
              <ui-radio
                class="circle pl-12 pr-12"
                :value="2"
                :label="t('Checking DNS')"
              />
              <ui-radio
                class="circle pl-12 pr-12"
                :value="3"
                :label="t('Workspace Selection')"
              />
            </ui-radio-group>
          </div>

          <div class="vertical-steps">
            <ul>
              <li>
                <p>1</p>
                <div class="vertical-steps__inner">
                  <span>{{
                    t(
                      `Read the instructions to create a CNAME record in your registrar's panel`,
                    )
                  }}</span>
                  <div class="d-flex align-items-end">
                    <ui-select
                      v-model="form.registar"
                      :label="t('Your registrar / DNS hosting provider')"
                      :items="registars"
                      item-text="name"
                      item-value="id"
                      :autocomplete="false"
                      :hideDetails="1"
                      class="flex-grow-1"
                    />

                    <ui-button
                      class="ml-8 btn-read"
                      :text="t('Read instruction')"
                      icon="fas fa-question-circle"
                      button-style="outline-accent"
                      :href="`https://docs.bemob.com/${locale}/custom-domain-settings`"
                    />
                  </div>
                </div>
              </li>
              <li>
                <p>2</p>
                <div class="vertical-steps__inner">
                  <ui-copy-input
                    :label="t('Create CNAME record for your domain that points to:')"
                    :model-value="store.user?.trackingDomainCNAME"
                  />
                  <ui-info>
                    <span
                      v-html="
                        t(
                          '<b>{0}</b> is your dedicated domain for CNAME linking to your custom tracking domains.',
                          [store.user?.trackingDomainCNAME],
                        )
                      "
                    ></span>
                    <br />
                    {{
                      t(
                        'We strongly recommend to use it instead of our other system domains.',
                      )
                    }}
                    <br />
                    <span
                      v-html="
                        t('The traffic is not sent directly to <b>{0}</b>', [
                          store.user?.trackingDomainCNAME,
                        ])
                      "
                    ></span>
                    <br />
                    {{
                      t(
                        'That’s why this domain has a very low chance to get into the databases of anti-viruses or be flagged as a spam, phishing, fraud-delivering.',
                      )
                    }}<br />
                    <span
                      v-html="
                        t(
                          `For more details click <a target='_blank' href='{0}'><b>here</b></a>`,
                          [`https://docs.bemob.com/${locale}/system-domains`],
                        )
                      "
                    ></span>
                  </ui-info>
                </div>
              </li>
              <li>
                <p>3</p>
                <div class="vertical-steps__inner">
                  <span>{{ t('Your CNAME record should look like this:') }}</span>
                  <v-card-text class="card_block mb-12">
                    <ui-info
                      >your.domain.name. CNAME
                      {{ store.user?.trackingDomainCNAME }}</ui-info
                    >
                  </v-card-text>
                </div>
              </li>
              <li>
                <p>4</p>
                <div class="vertical-steps__inner">
                  <span class="d-flex">{{
                    t('Please note that updating DNS records may take up to 24 hours')
                  }}</span>
                  <span>{{ t('Usually it takes 15-20 minutes') }}</span>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </template>

    <template v-if="step === 2">
      <div class="dialog-wrapper add-custom-domain">
        <div class="w100 pb-0">
          <div class="horizontal-steps">
            <ui-radio-group
              v-model="step"
              class="radio_group"
              styleType="horizontal-line"
            >
              <ui-radio
                class="circle pl-12 pr-12"
                :value="1"
                :label="t('Setting the CNAME')"
              />
              <ui-radio
                class="circle pl-12 pr-12"
                :value="2"
                :label="t('Checking DNS')"
              />
              <ui-radio
                class="circle pl-12 pr-12"
                :value="3"
                :label="t('Workspace Selection')"
              />
            </ui-radio-group>
          </div>
          <div class="vertical-steps vertical-steps__two">
            <div class="vertical-steps__inner p-0 mt-32">
              <span
                v-html="
                  t(
                    'Add your domain with no https:// or any other additional symbols e.g. www.mydomain.com<br>Then click on <b>Check DNS</b> button.',
                  )
                "
              />
              <div class="d-flex align-items-end domain_container">
                <ui-input
                  :hideDetails="1"
                  v-model="targetDomain"
                  :label="t('Your Domain')"
                  :placeholder="curentPlaceholder"
                  class="flex-grow-1"
                />

                <ui-button
                  class="ml-8 btn-read"
                  icon="fas fa-sync-alt"
                  :text="t('Check DNS')"
                  button-style="outline-accent"
                  :disabled="!targetDomain"
                  :loading="loadingDns"
                  @click="checkDns('loadingDns')"
                />
              </div>
              <div class="d-flex success-block" v-if="domainCheckingSuccess">
                {{ t('Domain configured properly') }}. {{ t(`Press 'Next' to proceed`) }}.
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template v-if="step === 3">
      <div class="dialog-wrapper add-custom-domain">
        <div class="w100 pb-0">
          <div xs12 class="horizontal-steps">
            <ui-radio-group
              v-model="step"
              class="radio_group"
              styleType="horizontal-line"
            >
              <ui-radio
                class="circle pl-12 pr-12"
                :value="1"
                :label="t('Setting the CNAME')"
              />
              <ui-radio
                class="circle pl-12 pr-12"
                :value="2"
                :label="t('Checking DNS')"
              />
              <ui-radio
                class="circle pl-12 pr-12"
                :value="3"
                :label="t('Workspace Selection')"
              />
            </ui-radio-group>
          </div>
          <div xs12 class="vertical-steps vertical-steps__two">
            <div class="vertical-steps__inner p-0 mt-32">
              <span>{{
                t(
                  'If you are using Workspaces it is required to select the corresponding WS for the added domain.',
                )
              }}</span>
              <div>
                <workspace-select
                  :label="`${t('Assigned Workspace')}*`"
                  :form="form"
                  @change-workspace="onChangeWorkspace"
                  multiple
                  chips
                  multi-line
                  :workspacesList="workspacesList"
                />
              </div>
              <div class="card_block mb-12">
                <ui-info>{{
                  t(
                    'The complete list of domains assigned to a workspace is available in Multi-User-Access -> Workspaces.',
                  )
                }}</ui-info>
              </div>
              <div class="d-flex success-block" v-if="domainCheckingSuccess">
                {{ t('Domain configured properly') }}. {{ t(`Press 'Next' to proceed`) }}.
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #footer v-if="step === 1">
      <section class="justify-end xs12" style="display: flex">
        <ui-button
          :text="t('Next')"
          icon-append="fas fa-arrow-right"
          button-style="accent"
          @click="nextStep"
        />
      </section>
    </template>

    <template #footer v-if="step === 2">
      <section class="justify-space-between xs12" style="display: flex; width: 100%">
        <div>
          <error-aggregator v-model="errors" v-model:active="errorsControlActive" />
        </div>
        <div class="justify-end d-flex">
          <ui-button :text="t('Back')" icon="fas fa-arrow-left" @click="step = 1" />

          <ui-button
            :text="t('Next')"
            icon-append="fas fa-arrow-right"
            button-style="accent"
            :loading="loading"
            @click="nextStep"
          />
        </div>
      </section>
    </template>
    <template #footer v-if="step === 3">
      <section class="justify-space-between xs12" style="display: flex; width: 100%">
        <div>
          <error-aggregator v-model="errors" v-model:active="errorsControlActive" />
        </div>
        <div class="justify-end d-flex">
          <ui-button :text="t('Back')" icon="fas fa-arrow-left" @click="step = 2" />

          <ui-button
            :text="t('Add Domain')"
            icon="fas fa-check"
            button-style="accent"
            :disabled="!domainCheckingSuccess && false"
            :loading="loading"
            @click="createDomain"
          />
        </div>
      </section>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue';
import i18n from '@/i18n';
import { useStore } from '@/store';
import { getUserFromApi } from '@/services/api/api-services/load-profile';
import { refreshUserWorkspaces } from '@/services/api/api-services/workspaces';
import { useI18n, useWorkspace } from '@/composables';
import { $emitter } from '@/services/emitter';
import {
  postCheckTrackingDomainDnsApiRequest,
  postCreateTrackingDomainApiRequest,
} from '@/services/api/domains-api';
import WorkspaceSelect from '@/views/reports/pickers/wokrspace-select.vue';
import { putUserWorkspaceApiRequest } from '@/services/api/workspaces-api';
import type { UserWorkspace } from '@/services/models';
import md5 from 'md5';
import type { AggregatedErrorItem } from '@/components/global/error-aggregator.vue';
import parseApiErrors from '@/utils/parseApiErrors';

defineProps<{
  data?: Record<string, unknown>;
}>();

const emit = defineEmits<{
  close: [];
}>();

const { t } = useI18n();
const store = useStore();
const show = ref(false);
const loading = ref(false);
const loadingDns = ref(false);
const step = ref(1);
const curentPlaceholder = ref('go.yourdomain.com');
const targetDomain = ref('');
const domainCheckingSuccess = ref(false);
const errors = ref<string | string[] | AggregatedErrorItem[] | null>(null);
const errorsControlActive = ref(false);
const oldHash = ref('');
const { workspacesList, defaultWorkspace } = useWorkspace({ mode: 'create' });

const form = reactive({
  registar: 'namecheap',
  domain: '',
  workspaceId: [] as string[],
});

const registars = ref<{ id: string; name: string }[]>([]);

const locale = computed(() => i18n.global.locale.value);

const getCurrentHash = () => md5(JSON.stringify(form));

const clearErrors = () => {
  errors.value = [];
  errorsControlActive.value = false;
  domainCheckingSuccess.value = false;
};

const showApiValidationError = (error: unknown) => {
  const responseMessage = (
    error as { response?: { data?: { message?: string } } }
  ).response?.data?.message;
  const messages = Object.values(parseApiErrors({ error })).flat();
  const items: AggregatedErrorItem[] = [];
  if (responseMessage) {
    items.push({ message: t(String(responseMessage)) });
  }
  messages.forEach((message) => {
    items.push({ message });
  });
  if (!items.length) return;
  errors.value = items;
  errorsControlActive.value = true;
};

const checkDns = async (loadingKey: 'loadingDns' | 'loading'): Promise<boolean> => {
  clearErrors();
  if (loadingKey === 'loadingDns') {
    loadingDns.value = true;
  } else {
    loading.value = true;
  }
  try {
    const data = (await postCheckTrackingDomainDnsApiRequest({
      domain: targetDomain.value,
    })) as {
      status?: string;
      error?: string;
      values?: { domain?: string; setTo?: string; shouldBe?: string };
    };
    if (data.status === 'FAIL') {
      errors.value = [
        {
          message: t(data.error ?? '', [
            data.values?.domain,
            data.values?.setTo,
            data.values?.shouldBe,
          ]),
        },
      ];
      errorsControlActive.value = true;
      return false;
    }
    domainCheckingSuccess.value = true;
    return true;
  } catch (error: unknown) {
    showApiValidationError(error);
    return false;
  } finally {
    if (loadingKey === 'loadingDns') {
      loadingDns.value = false;
    } else {
      loading.value = false;
    }
  }
};

const nextStep = async () => {
  if (step.value === 2 && !(await checkDns('loading'))) {
    return;
  }
  step.value = step.value + 1;
};

const onChangeWorkspace = () => {
  clearErrors();
};

const checkClose = () => {
  if (oldHash.value !== getCurrentHash()) {
    $emitter.emit('open-dialog', {
      name: 'ConfirmDialog',
      data: { onConfirm: () => emit('close') },
    });
  } else {
    emit('close');
  }
};

const createDomain = async () => {
  loading.value = true;
  clearErrors();
  const selectedWorkspaceIds = Array.isArray(form.workspaceId) ? form.workspaceId : [];

  if (!selectedWorkspaceIds.length) {
    errors.value = [{ message: t('{0} is required', [t('Assigned Workspace')]) }];
    errorsControlActive.value = true;
    loading.value = false;
    return;
  }

  try {
    await refreshUserWorkspaces();

    const created = (await postCreateTrackingDomainApiRequest({
      domain: targetDomain.value,
    })) as { id: string };

    const domainId = created.id;
    const mapWorkspaces = workspacesList.value.reduce<Record<string, UserWorkspace>>(
      (accumulator, item) => {
        accumulator[item.id] = item;
        return accumulator;
      },
      {},
    );
    const promises = selectedWorkspaceIds.map((id: string) => {
      const workspace = mapWorkspaces[id];
      const { name, trackingDomainIds = [] } = workspace;
      return putUserWorkspaceApiRequest({
        id,
        name,
        trackingDomainIds: [...trackingDomainIds, domainId],
      });
    });
    await Promise.all(promises);
    $emitter.emit('toast-success', t('Domain has been successfully added'));
    await getUserFromApi();
    emit('close');
  } catch (error: unknown) {
    showApiValidationError(error);
  } finally {
    loading.value = false;
  }
};

watch(step, async (value) => {
  clearErrors();
  if (value !== 3) {
    return;
  }
  await refreshUserWorkspaces();
});

onMounted(() => {
  void (async () => {
    try {
      await refreshUserWorkspaces();
    } catch {}

    const defaultDomainRow = store.trackingDomains.data.find(item => !item.custom);
    form.domain = defaultDomainRow?.domain ?? '';

    registars.value = [
      { id: 'namecheap', name: t('Namecheap') },
      { id: 'godaddy', name: t('GoDaddy') },
      { id: 'cloudflare', name: t('Cloudflare') },
      { id: 'other', name: t('Other') },
    ];

    const primaryWorkspace = defaultWorkspace.value;
    form.workspaceId = primaryWorkspace?.id ? [primaryWorkspace.id] : [];

    show.value = true;
    oldHash.value = getCurrentHash();
  })();
});
</script>

<style lang="scss" scoped>
@import '@/styles/pages/settings/domains';
@import '@/styles/dialogs/report-modals';

.add-custom-domain {
  .error-block {
    background: var(--color-red);
    color: var(--color-white);
    padding: 16px;
  }
  .success-block {
    background: var(--color-green);
    color: var(--color-white);
    padding: 16px;
  }
  > div {
    padding: 16px 24px 24px;
    max-height: 100%;
    overflow: auto;
  }
  .horizontal-steps {
    padding-bottom: 16px;
    border-bottom: 1px solid var(--color-gray20);
  }
  .vertical-steps {
    ul {
      list-style-type: none;
      padding-top: 16px;
      padding-left: 12px;
      > li {
        margin-bottom: -20px;
        p {
          position: relative;
          left: -12px;
          z-index: 1;
          display: inline-block;
          width: 24px;
          height: 24px;
          margin: 0;
          padding: 3px;
          text-align: center;
          border-radius: 20px;
          background: var(--color-gray20);
        }
      }
      li:last-child .vertical-steps__inner {
        border-left: 0;
      }
    }
    &__inner {
      position: relative;
      top: -20px;
      padding-left: 24px;
      padding-bottom: 16px;
      border-left: 1px solid var(--color-gray20);
      font-size: 13px;
      .v-text-field__details {
        display: none;
      }
      .btn-read {
        margin-right: 0;
        max-width: max-content;
        flex-shrink: 0 !important;
      }
      .copy-input-label {
        font-weight: normal;
      }
    }
  }
  .vertical-steps__two {
    .vertical-steps__inner {
      border: none;
    }
  }
}
</style>
